import { useEffect } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { StateResponse } from '@vinz/shared';
import { apiGet, UnauthorizedError } from './api-client.js';
import { useAuth } from './auth.js';
import type { HistoryResponse } from './routes/history.js';

// The poller sweeps every 5 minutes; polling more often than that would only
// ever show the same StateResponse. 30s keeps the page feeling live without
// hammering the API between sweeps.
const REFETCH_INTERVAL_MS = 30 * 1000;

/** An auth failure is recovered by refreshing the session, never by retrying the same stale token. */
export function apiQueryRetry(failureCount: number, error: Error): boolean {
  return !(error instanceof UnauthorizedError) && failureCount < 3;
}

/**
 * An UnauthorizedError is not a sign-out signal: it means this id token no longer authorizes, which
 * a refresh fixes. The queries carry idToken in their key, so once refreshSession swaps the token in
 * they re-run on their own. Only a failed refresh clears the session.
 */
function useRefreshOnUnauthorized<T>(query: UseQueryResult<T>): UseQueryResult<T> {
  const { refreshSession } = useAuth();
  useEffect(() => {
    if (query.error instanceof UnauthorizedError) void refreshSession();
  }, [query.error, refreshSession]);
  return query;
}

export function useStateQuery(idToken: string | null) {
  return useRefreshOnUnauthorized(useQuery({
    queryKey: ['state', idToken],
    queryFn: () => apiGet<StateResponse>('/state', idToken as string),
    enabled: idToken !== null,
    refetchInterval: REFETCH_INTERVAL_MS,
    retry: apiQueryRetry,
  }));
}

export interface HistoryQueryParams {
  service?: string;
  from?: string;
}

function historyPath({ service, from }: HistoryQueryParams): string {
  const qs = new URLSearchParams();
  if (service) qs.set('service', service);
  if (from) qs.set('from', from);
  const query = qs.toString();
  return query ? `/history?${query}` : '/history';
}

export function useHistoryQuery(idToken: string | null, params: HistoryQueryParams) {
  return useRefreshOnUnauthorized(useQuery({
    queryKey: ['history', params, idToken],
    queryFn: () => apiGet<HistoryResponse>(historyPath(params), idToken as string),
    enabled: idToken !== null,
    retry: apiQueryRetry,
  }));
}
